import { CloseCircleOutlined } from '@ant-design/icons'
import { endOfTime } from '@components/SupplyManagement/types'
import { startEndFormatter } from '@components/SupplyManagement/util'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { IconCircleCheckFilled, IconCoinOff, IconX } from '@tabler/icons-react'
import { isDefined } from '@utils/general'
import { startCase } from 'lodash'
import moment from 'moment'
import React, { useMemo } from 'react'

import type { StatusRowValue } from '../types'
import { prefillOutageFormWithRowInformation } from '../util'

type UpcomingOutagesPopoverProps = {
  rowValue: StatusRowValue
  closeOutagePopover: () => void
  openEditOutageDrawer: (outage: Gravitate.SupplyOutage, overrideIsForever?: boolean) => void
  cancelOutage: (outageId: string) => void
}

export function UpcomingOutagesPopover({
  rowValue,
  closeOutagePopover,
  openEditOutageDrawer,
  cancelOutage,
}: UpcomingOutagesPopoverProps) {
  const numActiveOutages = useMemo(
    () => rowValue.outages.filter((outage) => outage.status === 'active').length,
    [rowValue.outages]
  )

  function handleEditOutageDrawer(outage: Gravitate.SupplyOutage | null, overrideIsForever?: boolean) {
    if (!outage) {
      openEditOutageDrawer(prefillOutageFormWithRowInformation(rowValue), overrideIsForever)
    } else {
      openEditOutageDrawer(outage, overrideIsForever)
    }
    closeOutagePopover()
  }

  function handleCancelOutage(outageId: string) {
    closeOutagePopover()
    cancelOutage(outageId)
  }

  return (
    <div style={{ margin: '-12px -16px' }}>
      <Horizontal
        verticalCenter
        style={{ borderBottom: '1px solid var(--gray-200)', padding: '8px 12px' }}
        className='mb-3'
      >
        {rowValue.enabled ? (
          <>
            <IconCircleCheckFilled color='var(--theme-success)' size={18} />
            <Texto category='h5' className='mr-1 ml-1' style={{ flex: 1 }}>
              Supply Enabled
            </Texto>
          </>
        ) : (
          <>
            <IconCoinOff color='var(--theme-error)' size={18} />
            <Texto category='h5' className='mr-1 ml-1'>
              Supply Disabled
            </Texto>
            <Texto style={{ flex: 1 }} category='p2'>
              ({numActiveOutages} active outages)
            </Texto>
          </>
        )}

        <GraviButton onClick={closeOutagePopover} icon={<IconX size={18} />} appearance='text' />
      </Horizontal>
      <Vertical style={{ maxHeight: 300, overflowY: 'auto' }}>
        {rowValue.outages.map((outage) => {
          const scheduleType = outage.schedule_local.schedule_type
          return (
            <Vertical key={outage.id} className='mb-3 pl-3 pr-3'>
              <Horizontal>
                <Vertical>
                  <Horizontal verticalCenter>
                    <Texto weight={600} category='p2' className='mr-1'>
                      {outage.reason_code}
                    </Texto>
                    <Texto weight={400} category='p2' className='mr-2'>
                      ({startCase(scheduleType)})
                    </Texto>
                    <Texto
                      weight={600}
                      style={{
                        fontSize: 10,
                        borderRadius: 12,
                        backgroundColor: outage.status === 'active' ? 'var(--theme-color-2-dim)' : 'var(--gray-200)',
                        color: outage.status === 'active' ? 'var(--theme-color-2)' : 'var(--gray-600)',
                        padding: '1px 12px',
                      }}
                    >
                      {startCase(outage.status)}
                    </Texto>
                  </Horizontal>

                  <Texto category='label' weight={600}>
                    {startEndFormatter(scheduleType, outage.schedule_local.start_date, outage.timezone)} -{' '}
                    {startEndFormatter(scheduleType, outage.schedule_local.end_date || endOfTime, outage.timezone)}
                  </Texto>
                </Vertical>
                <GraviButton
                  style={{ borderRadius: '5px' }}
                  className='mr-2'
                  error={!outage.is_bulk}
                  icon={!outage.is_bulk && <CloseCircleOutlined />}
                  disabled={outage.is_bulk}
                  buttonText={outage.is_bulk ? 'Bulk Outage' : 'Cancel'}
                  onClick={() => handleCancelOutage(outage.id)}
                />
                <GraviButton
                  style={{ borderRadius: '5px' }}
                  buttonText='Edit'
                  onClick={() => handleEditOutageDrawer(outage)}
                />
              </Horizontal>
              {outage.schedule_local.schedule_type === 'recurring' && (
                <Horizontal style={{ flexWrap: 'wrap', gap: '0 8px' }}>
                  {outage.schedule_local.days.map((day, index) => (
                    <Texto key={day.day_of_week} style={{ fontSize: '10px' }}>
                      {startCase(day.day_of_week).slice(0, 3)}. {moment(day.start_time, 'HH:mm').format('hh:mm A')} -{' '}
                      {moment(day.end_time, 'HH:mm').format('hh:mm A')}
                      {index < outage.schedule_local.days.length - 1 && ','}
                    </Texto>
                  ))}
                </Horizontal>
              )}
              <Texto className='mt-1'>{outage.notes}</Texto>
            </Vertical>
          )
        })}
      </Vertical>
      {rowValue.outages.length === 0 && (
        <Texto style={{ marginBottom: '8px', padding: '0 12px' }}>No scheduled outages.</Texto>
      )}
      <Horizontal style={{ justifyContent: 'flex-end', padding: '8px 12px', borderTop: '1px solid var(--gray-200)' }}>
        <GraviButton
          style={{ borderRadius: '5px' }}
          buttonText='Create Outage'
          type='primary'
          theme1
          onClick={() => handleEditOutageDrawer(null, false)}
        />
      </Horizontal>
    </div>
  )
}
